import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'

import styles from './_styles/card-title.scss?inline'

/**
 * `dap-ds-card-title`
 * @summary Title of the card
 * @element dap-ds-card-title
 * @title - Card title
 * @group card
 *
 * @property {'sm' |'md' | 'lg'} size - The size of the card title. Default is `sm`.
 * @property {boolean} noPadding - Whether the card title should have no padding.
 * @property {'top' | 'bottom' | 'both' | 'none'} spacing - The spacing of the card title. This adds a margin to the card title. Default is `top`.
 *
 * @slot - The content of the title.
 *
 * @csspart base - The main card title container.
 *
 * @cssproperty --dds-card-title-color - The text color of the card title. Default is `var(--dds-color-text-primary)`.
 * @cssproperty --dds-card-title-font-family - The font family of the card title. Default is `var(--dds-font-family-base)`.
 * @cssproperty --dds-card-title-font-weight - The font weight of the card title. Default is `var(--dds-font-weight-bold)`.
 * @cssproperty --dds-card-title-line-height - The line height of the card title. Default is `var(--dds-font-line-height-large)`.
 * @cssproperty --dds-card-title-margin-bottom - The bottom margin of the card title. Default is `var(--dds-spacing-100)`.
 *
 * @cssproperty --dds-card-title-sm-padding-x - The horizontal padding for small size. Default is `var(--dds-spacing-400)`.
 * @cssproperty --dds-card-title-sm-padding-bottom - The bottom padding for small size. Default is `var(--dds-spacing-100)`.
 * @cssproperty --dds-card-title-sm-spacing-top - The top spacing for small size. Default is `var(--dds-spacing-400)`.
 * @cssproperty --dds-card-title-sm-spacing-bottom - The bottom spacing for small size. Default is `var(--dds-spacing-400)`.
 *
 * @cssproperty --dds-card-title-md-padding-x - The horizontal padding for medium size. Default is `var(--dds-spacing-400)`.
 * @cssproperty --dds-card-title-md-padding-bottom - The bottom padding for medium size. Default is `var(--dds-spacing-100)`.
 * @cssproperty --dds-card-title-md-spacing-top - The top spacing for medium size. Default is `var(--dds-spacing-400)`.
 * @cssproperty --dds-card-title-md-spacing-bottom - The bottom spacing for medium size. Default is `var(--dds-spacing-400)`.
 *
 * @cssproperty --dds-card-title-lg-padding-x - The horizontal padding for large size. Default is `var(--dds-spacing-600)`.
 * @cssproperty --dds-card-title-lg-padding-bottom - The bottom padding for large size. Default is `var(--dds-spacing-100)`.
 * @cssproperty --dds-card-title-lg-spacing-top - The top spacing for large size. Default is `var(--dds-spacing-600)`.
 * @cssproperty --dds-card-title-lg-spacing-bottom - The bottom spacing for large size. Default is `var(--dds-spacing-600)`.
 */
@customElement('dap-ds-card-title')
export default class DapDSCardTitle extends SizedMixin(DdsElement) {
  /** The base rendered root tag of the card title. */
  @property() renderAs = 'span'
  /** Whether the card title should be sized from the parent. */
  @property() parentSized: string = 'true'
  /** Whether the card title should have no padding. */
  @property({ type: Boolean }) noPadding: boolean

  static readonly styles = unsafeCSS(styles)

  constructor() {
    super()
    this.spacing = 'top'
  }

  render() {
    return html`
      <${unsafeStatic(this.renderAs)} part="base" class=${clsx(
        'card-title',
        `card-title--${this.size}`,
        `card-title-spacing--${this.spacing}`,
        {
          'card-title--no-padding': this.noPadding,
        },
      )}>
        <slot></slot>
      </${unsafeStatic(this.renderAs)}>
    `
  }
}
